import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import SearchCount from 'components/PublicTableElements/SearchCount';
import AuthMessage from '../../PublicTableElements/AuthMessage';
import NoDataTable from 'components/common/NoDataTable';
import Loading from 'components/Loading';
import { useMedia } from 'hooks/useMedia';

import styles from './styles.module.scss';
import TablesConstructorPopup from './Popup';
import { SeenEntityService } from 'service/SeenEntityService';

export const TRANSPORT_TYPE = 'transport';

export default function TableConstructor({
  resultsCountShow,
  data,
  labels,
  InfoComponent,
  service,
  updateData,
  noDataMessage,
  urlToAdd,
  type,
  selectedId,
  onSelect,
  searchCoordinates,
  isLoading = false,
  showAddButton = true,
}) {
  const authenticated = useSelector((state) => state.auth.authenticated);
  const profileData = useSelector((state) => state.profile.profileData);
  const { t } = useTranslation();
  const { isDesktop } = useMedia();

  const [renderData, setRenderData] = useState([]);

  const [showAuthMessage, setShowAuthMessage] = useState(false);

  const onRowClick = useCallback(
    (el) => (_e) => {
      if (authenticated) {
        if (!el.is_seen) {
          SeenEntityService.markAsSeen(el.id);
        }

        if (onSelect) {
          onSelect(el.id);
        }
      } else {
        setShowAuthMessage(true);
      }
    },
    [authenticated, onSelect],
  );

  const handleClose = () => {
    if (onSelect) {
      onSelect(null);
    }
  };

  const getRows = useCallback(() => {
    const backgroundRow = (row) => {
      // Не помечать серым свои собственные карточки
      // Проверяем разные поля для ID создателя
      const isMyCard = profileData && (
        row.user_id === profileData.id ||
        row.employee_id === profileData.id ||
        (row.user && row.user.id === profileData.id)
      );

      return [
        styles.table__row,
        'isActiveProposal' in row && row.isActiveProposal
          ? styles['table__row--activeProposal']
          : (row.isExpired || ('is_active' in row && !row.is_active)) && styles['table__row--expired'],
        row.is_seen && !isMyCard && styles['table__row--viewed'],
      ];
    };
    return data.items.map((el, rowIndex) => (
      <div className={cn(...backgroundRow(el))} key={'row' + rowIndex} onClick={onRowClick(el)}>
        {labels.map((item, index) => {
          let contentProps = {};
          item.data.forEach((dataType) => {
            contentProps[dataType] = el[dataType];
          });
          return (
            <div
              className={styles.table__column}
              key={index}
              data-label={item.label}
              style={isDesktop && item.width ? { flex: `0 0 ${item.width}`, maxWidth: item.width } : undefined}
            >
              <div className={cn(styles.column__content, item.topAlign && styles['column__content--topAlign'])}>
                <span className={styles.column__label}>{item.label}</span>
                <div className={styles.column__value}>
                  <item.component content={contentProps} align={item.align} id={el.id} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    ));
  }, [data, labels, onRowClick, isDesktop, profileData]);

  useEffect(() => {
    setRenderData(getRows());
  }, [getRows]);

  return (
    <>
      {showAuthMessage ? <AuthMessage open={true} onClose={() => setShowAuthMessage(false)} /> : ''}
      {resultsCountShow && <SearchCount count={data.count} />}
      <div className={styles.table}>
        <div className={styles.table__header}>
          {labels.map((el, index) => (
            <div
              className={styles.table__headerColumn}
              key={'label' + index}
              style={isDesktop && el.width ? { flex: `0 0 ${el.width}`, maxWidth: el.width } : undefined}
            >
              {el.label}
            </div>
          ))}
        </div>
        {isLoading ? (
          <div className={cn(styles.tableBody, styles.tableBodyLoading)}>
            <Loading />
          </div>
        ) : data.count > 0 ? (
          <div className={styles.tableBody}>
            {renderData}
          </div>
        ) : (
          <NoDataTable message={noDataMessage} urlToAdd={urlToAdd} showButton={showAddButton} />
        )}
      </div>

      <TablesConstructorPopup
        data={data}
        service={service}
        updateData={updateData}
        selectedCargoId={selectedId}
        onClose={handleClose}
        InfoComponent={InfoComponent}
        searchCoordinates={searchCoordinates}
      />
    </>
  );
}
